/**
 * SyncStatusBar — always-visible sticky bar at the bottom of the POS.
 *
 * States it surfaces (in priority order):
 *   1. OFFLINE — no internet at all
 *   2. CONFLICTS — items need operator resolution (count > 0)
 *   3. SYNCING — items in flight or pending
 *   4. ONLINE — all caught up
 *
 * Click to expand a panel showing the queue. From there, operator can:
 *   - Tap "Sync now" to force a drain
 *   - Tap a conflict to open the resolution drawer
 *   - See a per-item status badge
 *
 * Mounting:
 *   Place near the root inside <OfflineProvider>, e.g. in AuthedLayout
 *   so it shows on every authed page. The bar is fixed bottom and
 *   doesn't push other content.
 */
import React, { useState } from "react";
import {
  Wifi,
  WifiOff,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  RefreshCcw,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { useOffline } from "@/context/OfflineProvider";
import { useOutbox } from "@/hooks/useOutbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConflictResolutionDrawer } from "./ConflictResolutionDrawer";
import type { OutboxItem } from "@/offline/db";

// ── Top-level bar ───────────────────────────────────────────────

export const SyncStatusBar: React.FC = () => {
  const { status, syncNow, scope } = useOffline();
  const { companyId, businessId, userId } = scope;

  const { items, stats } = useOutbox({ companyId, businessId });
  const [expanded, setExpanded] = useState(false);
  const [conflictDrawerId, setConflictDrawerId] = useState<string | null>(null);

  // Don't render if no business is active (e.g. on /portfolio)
  if (!companyId || !businessId) return null;

  // ── State classification ───────────────────────────────────
  const conflicts = stats?.conflict ?? 0;
  const queued = (stats?.pending ?? 0) + (stats?.failed ?? 0);
  const inFlight = stats?.in_flight ?? 0;

  let state: "offline" | "conflict" | "syncing" | "online" = "online";
  if (!status.online) state = "offline";
  else if (conflicts > 0) state = "conflict";
  else if (queued > 0 || inFlight > 0) state = "syncing";

  const visibleItems = items.filter((i) => i.status !== "done");
  // Sales made by someone else on this till wait for them to sign in.
  const othersWaiting = visibleItems.filter((i) => userId && i.userId && i.userId !== userId).length;
  const showBar = state !== "online" || visibleItems.length > 0;
  if (!showBar) return null;

  // ── Color theme per state ──────────────────────────────────
  const theme = {
    offline: {
      bg: "bg-[#0A2540]",
      text: "text-white",
      accent: "text-rose-300",
      Icon: WifiOff,
      label: "No internet — keep selling (cash)",
      sub: queued ? `${queued} sale${queued === 1 ? "" : "s"} saved on this till, sent when the internet is back` : "Cash sales are saved on this till until the internet is back",
    },
    conflict: {
      bg: "bg-amber-50",
      text: "text-amber-900",
      accent: "text-amber-700",
      Icon: AlertTriangle,
      label: `${conflicts} sale${conflicts === 1 ? "" : "s"} need${conflicts === 1 ? "s" : ""} attention`,
      sub: `Tap for details${queued ? ` · ${queued} still to send` : ""}`,
    },
    syncing: {
      bg: "bg-[#4CAF50]/10",
      text: "text-[#0A2540]",
      accent: "text-[#4CAF50]",
      Icon: Loader2,
      label: "Sending offline sales",
      sub: `${queued + inFlight} to go${othersWaiting ? ` · ${othersWaiting} wait for another cashier to sign in` : ""}`,
    },
    online: {
      bg: "bg-card",
      text: "text-muted-foreground",
      accent: "text-emerald-600",
      Icon: CheckCircle2,
      label: "Online",
      sub: "all caught up",
    },
  }[state];

  return (
    <>
      {/* Sticky bar */}
      <div
        className={`fixed bottom-0 inset-x-0 z-40 border-t shadow-lg ${theme.bg} ${theme.text}`}
        role="status"
        aria-live="polite"
      >
        {/* Collapsed row */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50]"
        >
          <theme.Icon
            className={`h-4 w-4 ${theme.accent} ${
              state === "syncing" ? "animate-spin" : ""
            }`}
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{theme.label}</div>
            <div className="text-[10px] opacity-80">{theme.sub}</div>
          </div>
          {queued + inFlight + conflicts > 0 && (
            <Badge
              variant="outline"
              className={`tabular-nums ${
                state === "conflict"
                  ? "border-amber-300 bg-amber-100 text-amber-900"
                  : state === "offline"
                    ? "border-white/30 bg-white/10 text-white"
                    : "border-[#4CAF50]/40 bg-[#4CAF50]/10 text-[#4CAF50]"
              }`}
            >
              {queued + inFlight + conflicts}
            </Badge>
          )}
          {expanded ? (
            <ChevronDown className="h-4 w-4 opacity-70" />
          ) : (
            <ChevronUp className="h-4 w-4 opacity-70" />
          )}
        </button>

        {/* Expanded panel */}
        {expanded && (
          <div className="border-t border-current/10 max-h-[60vh] overflow-y-auto">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <p className="text-xs opacity-80">
                {visibleItems.length} waiting on this till
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => syncNow()}
                disabled={!status.online}
                className={`gap-2 ${
                  state === "offline"
                    ? "hover:bg-white/15 text-white"
                    : "hover:bg-black/5"
                }`}
              >
                <RefreshCcw className="h-3 w-3" />
                Sync now
              </Button>
            </div>

            <div className="divide-y divide-current/10">
              {visibleItems.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs opacity-60">
                  Nothing waiting
                </p>
              ) : (
                visibleItems.map((item) => (
                  <QueueRow
                    key={item.id}
                    item={item}
                    onResolve={() => setConflictDrawerId(item.id)}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Spacer so content isn't hidden behind the bar */}
      <div className="h-14" aria-hidden />

      <ConflictResolutionDrawer
        itemId={conflictDrawerId}
        onClose={() => setConflictDrawerId(null)}
      />
    </>
  );
};

// ── One queue row ───────────────────────────────────────────────

const QueueRow: React.FC<{
  item: OutboxItem;
  onResolve: () => void;
}> = ({ item, onResolve }) => {
  const meta = statusMeta(item.status);
  const ageMs = Date.now() - item.createdAt;
  const ageLabel = formatAge(ageMs);

  return (
    <div className="px-4 py-2.5 flex items-center gap-3">
      <meta.Icon
        className={`h-3.5 w-3.5 shrink-0 ${meta.tone} ${
          item.status === "in_flight" ? "animate-spin" : ""
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{item.summary}</div>
        <div className="text-[10px] opacity-70 flex items-center gap-2">
          <span>{meta.label}</span>
          <span>·</span>
          <span>{ageLabel}</span>
          {item.attempts > 0 && (
            <>
              <span>·</span>
              <span>
                {item.attempts} attempt{item.attempts === 1 ? "" : "s"}
              </span>
            </>
          )}
        </div>
        {item.lastError && (
          <div className="text-[10px] text-rose-400 mt-0.5 truncate">
            {item.lastError}
          </div>
        )}
      </div>
      {item.status === "conflict" && (
        <Button size="sm" variant="outline" onClick={onResolve}>
          See why
        </Button>
      )}
    </div>
  );
};

// ── Helpers ─────────────────────────────────────────────────────

function statusMeta(status: OutboxItem["status"]) {
  switch (status) {
    case "pending":
      return { Icon: Wifi, label: "Waiting to send", tone: "text-current opacity-70" };
    case "in_flight":
      return { Icon: Loader2, label: "Sending", tone: "text-[#4CAF50]" };
    case "failed":
      return { Icon: WifiOff, label: "Will try again", tone: "text-amber-500" };
    case "conflict":
      return {
        Icon: AlertTriangle,
        label: "Needs attention",
        tone: "text-amber-600",
      };
    case "done":
      return { Icon: CheckCircle2, label: "Sent", tone: "text-emerald-600" };
  }
}

function formatAge(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default SyncStatusBar;
